import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type {
	HouseholdMember,
	Todo,
	TodoCategory,
	TodoTitle,
	UserId,
} from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { TodoItem } from './TodoItem.tsx'
import styles from './Board.module.css'

type Props = {
	category: TodoCategory | null
	todos: Todo[]
	members: HouseholdMember[]
	memberMap: Map<UserId, HouseholdMember>
	onToggle: (todo: Todo, next: boolean) => void
	onRename: (todo: Todo, title: TodoTitle) => void
	onTagClick: (todo: Todo) => void
	onPriorityToggle: (todo: Todo) => void
	onAssign: (todo: Todo, assigneeIds: UserId[]) => void
	onClearCompleted: () => void
}

// One board lane: a fixed header, the active cards (incoming priority-first /
// newest order), then a collapsible "Done" section. Adding cards is handled by
// the board-level FAB composer, not here.
export const Column = ({
	category,
	todos,
	members,
	memberMap,
	onToggle,
	onRename,
	onTagClick,
	onPriorityToggle,
	onAssign,
	onClearCompleted,
}: Props) => {
	const online = useOnline()
	const [showDone, setShowDone] = useState(false)

	const color = category ? colorFor(category) : 'var(--ink-muted)'

	const active = useMemo(() => todos.filter((t) => !t.completed), [todos])
	const done = useMemo(
		() =>
			todos
				.filter((t) => t.completed)
				.sort((a, b) =>
					(b.completedAt ?? '').localeCompare(a.completedAt ?? ''),
				),
		[todos],
	)

	const itemProps = (todo: Todo) => ({
		todo,
		members,
		memberMap,
		onToggle: (next: boolean) => onToggle(todo, next),
		onRename: (title: TodoTitle) => onRename(todo, title),
		onTagClick: () => onTagClick(todo),
		onPriorityToggle: () => onPriorityToggle(todo),
		onAssign: (ids: UserId[]) => onAssign(todo, ids),
	})

	return (
		<section className={styles.column}>
			<header className={styles.colHeader}>
				<span
					className={styles.dot}
					style={{ background: color }}
					aria-hidden="true"
				/>
				<h2 className={styles.colName}>{category ?? 'general'}</h2>
				<span className={styles.count}>{active.length}</span>
			</header>

			<div className={styles.cards}>
				<ul className={styles.list}>
					{active.map((todo) => (
						<TodoItem key={todo.id} {...itemProps(todo)} />
					))}
				</ul>

				{active.length === 0 && done.length === 0 && (
					<p className={styles.colEmpty}>No cards yet</p>
				)}

				{done.length > 0 && (
					<div className={styles.done}>
						<button
							type="button"
							className={styles.doneToggle}
							onClick={() => setShowDone((s) => !s)}
							aria-expanded={showDone}
						>
							{showDone ? (
								<ChevronDown size={16} aria-hidden="true" />
							) : (
								<ChevronRight size={16} aria-hidden="true" />
							)}
							Done ({done.length})
						</button>

						{showDone && (
							<>
								<ul className={styles.list}>
									{done.map((todo) => (
										<TodoItem
											key={todo.id}
											{...itemProps(todo)}
										/>
									))}
								</ul>
								<button
									type="button"
									className={styles.clear}
									onClick={onClearCompleted}
									disabled={!online}
								>
									Clear completed
								</button>
							</>
						)}
					</div>
				)}
			</div>
		</section>
	)
}
